import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight, ArrowDown, BookOpen, Volume2, Repeat, ShieldCheck, ShieldX, Brain, Flame, Quote, X, Search, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// ─────────────────────────────────────────────────────────────────────────────
// The PDF download card — same flow as Resources.jsx but rendered larger and
// more prominent because this page exists to convert.
// ─────────────────────────────────────────────────────────────────────────────
const PdfCard = ({ freebie, compact = false }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    useEffect(() => {
        if (!freebie) return;
        const saved = localStorage.getItem(`mw_freebie_${freebie.id}`);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setUnlocked(true);
                setPdfUrl(parsed.pdf_url);
            } catch (_) {}
        }
    }, [freebie]);

    const triggerDownload = async (url, title) => {
        const filename = `${title.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-")}.pdf`;
        try {
            const res = await fetch(url, { mode: "cors" });
            if (!res.ok) throw new Error("fetch failed");
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 4000);
        } catch (_) {
            try {
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } catch (_) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !firstName.trim() || !lastName.trim()) return;
        setLoading(true);
        try {
            const res = await api.post("/freebies/claim", {
                email,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                freebie_id: freebie.id,
            });
            const data = res.data;
            setPdfUrl(data.pdf_url);
            setUnlocked(true);
            localStorage.setItem(`mw_freebie_${freebie.id}`, JSON.stringify({ pdf_url: data.pdf_url, email }));
            triggerDownload(data.pdf_url, freebie.title);
            // Meta Pixel — fire Lead event so Facebook ad algo learns from converters
            if (typeof window !== "undefined" && window.fbq) {
                window.fbq("track", "Lead", {
                    content_name: freebie.title,
                    content_category: "Freebie",
                    value: 0,
                    currency: "USD",
                });
            }
            toast.success("Unlocked. Your PDF is downloading — check your email too.");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not unlock. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!freebie) return null;

    return (
        <div
            className={`bg-black border border-red-700/50 shadow-[0_0_60px_-15px_rgba(185,28,28,0.5)] overflow-hidden flex flex-col ${compact ? "" : "md:flex-row"}`}
            data-testid="freetools-pdf-card"
        >
            {freebie.thumb_url && (
                <div className={`relative ${compact ? "" : "md:w-2/5"} bg-[#050505] overflow-hidden flex-shrink-0`}>
                    <img
                        src={freebie.thumb_url}
                        alt={`${freebie.title} preview`}
                        loading="lazy"
                        draggable={false}
                        className={`w-full ${compact ? "h-56 sm:h-64" : "h-72 md:h-full"} object-cover object-top select-none pointer-events-none`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60 pointer-events-none" />
                    <div className="absolute top-4 left-4 bg-red-700 px-3 py-1 font-stencil text-white text-[10px] tracking-widest">
                        PREVIEW
                    </div>
                    <div className="absolute top-4 right-4 font-stencil text-red-500 text-[10px] tracking-widest bg-black/70 px-3 py-1 border border-red-700/40">
                        FREE
                    </div>
                </div>
            )}

            <div className={`${compact ? "p-6 md:p-7" : "p-8 md:p-10"} flex flex-col flex-1`}>
                <div className="font-stencil text-red-600 text-[11px] tracking-[0.35em] uppercase">
                    The Field Manual
                </div>
                <h3 className={`mt-3 font-display ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"} uppercase tracking-tight leading-[0.95]`}>
                    {freebie.title}
                </h3>
                <p className={`mt-4 text-white/70 leading-relaxed ${compact ? "text-sm" : "flex-1"}`}>
                    {freebie.description}
                </p>

                {!unlocked ? (
                    <form onSubmit={submit} className="mt-6 space-y-3" data-testid="freetools-form">
                        <div className="grid grid-cols-2 gap-3">
                            <input
                                type="text"
                                required
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First name"
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="freetools-first-name"
                                autoComplete="given-name"
                            />
                            <input
                                type="text"
                                required
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last name"
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="freetools-last-name"
                                autoComplete="family-name"
                            />
                        </div>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                            data-testid="freetools-email"
                            autoComplete="email"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-primary justify-center text-base disabled:opacity-50"
                            data-testid="freetools-submit"
                        >
                            <Download size={18} />
                            {loading ? "Sending…" : "Get the PDF — Free"}
                        </button>
                        <div className="text-[11px] text-white/40 text-center">
                            We'll send the PDF to your email. No spam — unsubscribe anytime.
                        </div>
                    </form>
                ) : (
                    <div className="mt-6 space-y-3">
                        <div className="text-green-500 text-sm font-stencil tracking-widest">
                            ✓ UNLOCKED
                        </div>
                        <button
                            type="button"
                            onClick={() => triggerDownload(pdfUrl, freebie.title)}
                            className="w-full btn-primary justify-center text-base"
                            data-testid="freetools-download"
                        >
                            <Download size={18} /> Download Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Re-usable section header
// ─────────────────────────────────────────────────────────────────────────────
const Eyebrow = ({ children, testId }) => (
    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase" data-testid={testId}>
        <span className="h-px w-12 bg-red-600" />
        {children}
    </div>
);

const SectionTitle = ({ children, accent }) => (
    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
        {children} {accent && <span className="text-red-600">{accent}</span>}
    </h2>
);

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
const FreeTools = () => {
    const [freebie, setFreebie] = useState(null);

    // ── Meta Pixel (Facebook) — fires only on this page ──────────────────────
    useEffect(() => {
        // Avoid double-loading if user navigates back to this page
        if (!window.fbq) {
            /* eslint-disable */
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            window.fbq('init', '1898073364212822');
            /* eslint-enable */
        }
        window.fbq && window.fbq('track', 'PageView');

        // <noscript> fallback pixel (for users who block JS — still tracks impression)
        const noscriptImg = document.createElement('img');
        noscriptImg.height = 1;
        noscriptImg.width = 1;
        noscriptImg.style.display = 'none';
        noscriptImg.src = 'https://www.facebook.com/tr?id=1898073364212822&ev=PageView&noscript=1';
        noscriptImg.setAttribute('data-meta-pixel-noscript', 'true');
        document.body.appendChild(noscriptImg);

        return () => {
            if (noscriptImg.parentNode) noscriptImg.parentNode.removeChild(noscriptImg);
        };
    }, []);

    useEffect(() => {
        api.get("/freebies").then((r) => {
            const list = r.data || [];
            setFreebie(list[0] || null);
        }).catch(() => setFreebie(null));
    }, []);

    const scrollToPdf = () => {
        const el = document.getElementById("get-pdf");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const fourRs = [
        {
            Icon: Search,
            letter: "R",
            title: "Recognize",
            text: "You can't fight what you refuse to acknowledge. Notice the thought. Name it. Don't ignore it. Bring it to the light. Key question — what thought am I entertaining right now?",
            verse: "Romans 12:2",
        },
        {
            Icon: ShieldX,
            letter: "R",
            title: "Reject",
            text: "Not every thought deserves a seat in your mind. Test it with Scripture. Is it true? Is it helpful? Is it from God? Key question — does this thought align with God's truth?",
            verse: "1 John 4:1",
        },
        {
            Icon: BookOpen,
            letter: "R",
            title: "Replace",
            text: "Replace the lie with God's truth. Speak truth. Meditate on truth. Write it down. Declare it out loud. Key question — what is God's truth about this situation or thought?",
            verse: "Philippians 4:8",
        },
        {
            Icon: RotateCw,
            letter: "R",
            title: "Repeat",
            text: "The battle is won one thought at a time. Stay consistent. Build the habit. Keep taking thoughts captive daily. Key question — will I stay consistent until this becomes my pattern?",
            verse: "2 Corinthians 10:5",
        },
    ];

    const verses = [
        { ref: "Romans 12:2", quote: "Do not conform to the pattern of this world, but be transformed by the renewing of your mind." },
        { ref: "Joshua 1:8", quote: "Keep this Book of the Law always on your lips; meditate on it day and night... then you will be prosperous and successful." },
        { ref: "Proverbs 18:21", quote: "The tongue has the power of life and death, and those who love it will eat its fruit." },
        { ref: "2 Corinthians 10:5", quote: "We demolish arguments and every pretension that sets itself up against the knowledge of God, and we take captive every thought to make it obedient to Christ." },
        { ref: "Mark 11:23", quote: "Truly I tell you, if anyone says to this mountain, 'Go, throw yourself into the sea,' and does not doubt... it will be done." },
        { ref: "Psalm 1:2", quote: "But whose delight is in the law of the Lord, and who meditates on his law day and night." },
    ];

    const lies = [
        { lie: "My past defines me.", truth: "If anyone is in Christ, he is a new creation.", ref: "2 Corinthians 5:17" },
        { lie: "I am trapped.", truth: "Where the Spirit of the Lord is, there is freedom.", ref: "2 Corinthians 3:17" },
        { lie: "I can't handle this.", truth: "I can do all things through Christ who strengthens me.", ref: "Philippians 4:13" },
        { lie: "I am forgotten.", truth: "I know the plans I have for you, declares the Lord.", ref: "Jeremiah 29:11" },
        { lie: "I am worthless.", truth: "See, I have engraved you on the palms of my hands.", ref: "Isaiah 49:16" },
        { lie: "I am alone in this battle.", truth: "Be strong and courageous. The Lord goes with you.", ref: "Deuteronomy 31:6" },
        { lie: "My anxiety will win.", truth: "Cast all your anxiety on Him, for He cares for you.", ref: "1 Peter 5:7" },
        { lie: "The darkness is stronger than me.", truth: "Greater is He who is in you than he who is in the world.", ref: "1 John 4:4" },
    ];

    const steps = [
        { n: "01", title: "Print it. Tape it where you'll see it.", text: "Bathroom mirror. Truck dashboard. The inside of your locker. Make the truth unavoidable — the way the lies have been." },
        { n: "02", title: "Speak one out loud every morning.", text: "Not in your head. Out loud. Your ears need to hear your own mouth declaring it. Your brain logs the difference." },
        { n: "03", title: "When the lie hits, declare the opposite.", text: "Anxiety: \"I am not given a spirit of fear.\" Shame: \"There is no condemnation.\" Don't argue with the lie. Drown it." },
        { n: "04", title: "Repeat for 30 days. Watch what shifts.", text: "Neural pathways need reps. The first week feels mechanical. By week three, you'll start to mean it. By day thirty, something inside you has moved." },
    ];

    return (
        <div className="bg-black text-white" data-testid="freetools-page">

            {/* ═══════════════ HERO ═══════════════ */}
            <section
                className="relative overflow-hidden border-b border-white/10"
                data-testid="freetools-hero"
            >
                {/* Backdrop */}
                <div className="absolute inset-0 opacity-30">
                    <div
                        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(185,28,28,0.5),transparent_60%)]"
                    />
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(180deg,#000,rgba(0,0,0,0.5),#000)]" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 md:pt-28 pb-16 md:pb-20">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                        <div className="lg:col-span-7">
                            <Eyebrow testId="freetools-eyebrow">Free Tool · The Battle Drill</Eyebrow>
                            <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl">
                                Win the war <br />
                                in your <br />
                                <span className="text-red-600">mind.</span>
                            </h1>
                            <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
                                A simple 4-step battle drill — Recognize, Reject, Replace, Repeat. The framework that breaks the lie before it takes root.
                            </p>
                            <p className="mt-4 max-w-2xl text-white/55 leading-relaxed">
                                Plus 30 of the lies you've been believing — paired with the biblical truth that dismantles each one. Print it. Speak it. Rewire what the lies installed.
                            </p>
                            <div className="mt-10 hidden lg:flex items-center gap-2 text-white/40 text-xs font-stencil tracking-widest animate-pulse">
                                <ArrowDown size={14} /> KEEP READING
                            </div>
                        </div>

                        {/* Hero PDF claim card — moved up so the form is immediately visible */}
                        <div className="lg:col-span-5 lg:pl-2" data-testid="freetools-hero-card">
                            <PdfCard freebie={freebie} compact />
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ WATCH TIM — moved out of hero so form is above the fold ═══════════════ */}
            {freebie && (
                <section className="bg-[#050505] py-16 md:py-20 border-b border-white/10" data-testid="freetools-watch">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="grid lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-5">
                                <Eyebrow>Why repetition wins</Eyebrow>
                                <SectionTitle accent="loud.">Truth has to be</SectionTitle>
                                <p className="mt-6 text-white/70 leading-relaxed">
                                    The accuser repeated his lie until it sounded like your own voice. So you'll repeat the truth — until what God says about you drowns him out.
                                </p>
                            </div>
                            <div className="lg:col-span-5 lg:col-start-8">
                                <div className="border-2 border-red-700 bg-black overflow-hidden shadow-[0_0_80px_-20px_rgba(185,28,28,0.6)] max-w-sm mx-auto" data-testid="freetools-hero-video">
                                    <div className="relative aspect-[9/16] bg-[#050505]">
                                        <video
                                            src="/freetools-hero.mp4"
                                            poster="/freetools-hero-poster.jpg"
                                            className="w-full h-full object-cover"
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            controls
                                            preload="metadata"
                                            data-testid="freetools-hero-video-player"
                                        >
                                            Sorry, your browser can't play this video. Tap the button above to grab the PDF directly.
                                        </video>
                                        <div className="absolute top-3 left-3 bg-red-700 px-3 py-1 font-stencil text-white text-[10px] tracking-widest pointer-events-none">
                                            WATCH
                                        </div>
                                    </div>
                                    <div className="bg-[#0a0a0a] border-t border-red-700/40 p-5 text-center">
                                        <Quote size={18} className="text-red-600 mx-auto" strokeWidth={1.5} />
                                        <p className="mt-3 font-display text-base leading-snug uppercase tracking-tight">
                                            "Death and life are in the <span className="text-red-600">power of the tongue.</span>"
                                        </p>
                                        <p className="mt-2 text-[10px] font-stencil tracking-[0.3em] text-white/50">PROVERBS 18:21</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ═══════════════ WHY REPETITION MATTERS ═══════════════ */}
            <section className="bg-black py-24 md:py-32 border-b border-white/10" data-testid="freetools-why">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <Eyebrow>The Problem</Eyebrow>
                            <SectionTitle accent="repeated.">The lie was</SectionTitle>
                            <p className="mt-8 text-white/70 text-lg leading-relaxed">
                                For years. By the people who were supposed to protect you. By the screen in your pocket. By the voice in your head that learned to sound like yours.
                            </p>
                            <p className="mt-4 text-white/70 leading-relaxed">
                                That's why it's lodged so deep. Repetition.
                            </p>
                        </div>

                        <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-white/10">
                            <p className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-tight">
                                If the lie got there by being said a thousand times — <span className="text-red-600">the truth doesn't get there in one.</span>
                            </p>
                            <div className="mt-10 space-y-6 text-white/70 leading-relaxed">
                                <p>
                                    Your brain is not a hard drive. It's a forest. The paths you walk most become the easiest to walk again. Neuroscientists call it <span className="text-white">myelination</span> — repeated firing wraps the nerve in insulation, and what once took effort becomes automatic.
                                </p>
                                <p>
                                    The thoughts you rehearse — even the ones that ruin you — become the highways of your mind. <span className="text-white">"I always fail." "I'm too much." "Nobody stays."</span> Said often enough, they stop sounding like opinions. They start sounding like facts.
                                </p>
                                <p>
                                    But the brain is also <span className="text-white">plastic</span>. Pathways can be cut. New ones can be carved. What it requires isn't a single moment of belief — it's deliberate, repeated, out-loud rehearsal of a better word.
                                </p>
                                <p className="text-white font-medium">
                                    God knew this before the neuroscientists did. He said it 2,500 years ago through a guy named Joshua.
                                </p>
                                <div className="border-l-2 border-red-700 pl-6 py-2">
                                    <p className="italic text-white/85">"Keep this Book of the Law always on your lips; meditate on it day and night... then you will be prosperous and successful."</p>
                                    <p className="mt-2 text-xs font-stencil tracking-[0.3em] text-red-600">JOSHUA 1:8</p>
                                </div>
                                <p>
                                    Not <span className="italic">read it once</span>. Not <span className="italic">agree with it</span>. Keep it <span className="text-white">on your lips</span>. Day and night. Until the word He spoke over you is the loudest word in the room.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════ THE FOUR FORCES ═══════════════ */}
            <section className="bg-[#050505] py-24 md:py-32 border-b border-white/10" data-testid="freetools-forces">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Eyebrow>The Battle Drill</Eyebrow>
                    <SectionTitle accent="Rs.">The Four</SectionTitle>
                    <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
                        A simple framework to win the war in your mind. Four moves. Repeated until the lie has nowhere left to land.
                    </p>

                    <div className="mt-16 grid md:grid-cols-2 gap-6">
                        {fourRs.map((r, i) => (
                            <div
                                key={r.title}
                                className="group bg-black border border-white/10 hover:border-red-700 transition-colors p-8 md:p-10"
                                data-testid={`r-${r.title.toLowerCase()}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="bg-red-700/15 border border-red-700/40 p-3">
                                        <r.Icon size={26} className="text-red-600" strokeWidth={1.5} />
                                    </div>
                                    <span className="font-display text-white/15 text-5xl leading-none">0{i + 1}</span>
                                </div>
                                <div className="mt-6 flex items-baseline gap-3">
                                    <span className="font-display text-5xl md:text-6xl text-red-600 leading-none">{r.letter}</span>
                                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight">
                                        {r.title}
                                    </h3>
                                </div>
                                <p className="mt-4 text-white/65 leading-relaxed">{r.text}</p>
                                <div className="mt-6 text-xs font-stencil tracking-[0.3em] text-red-600">
                                    {r.verse}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ SCRIPTURE WALL ═══════════════ */}
            <section className="bg-black py-24 md:py-32 border-b border-white/10" data-testid="freetools-scripture">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Eyebrow>The Biblical Foundation</Eyebrow>
                    <SectionTitle accent="said it first.">God</SectionTitle>
                    <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
                        This isn't a self-help technique with a Christian sticker on it. Speaking the Word over your life is a command — repeated across Scripture, written into how you were made.
                    </p>

                    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                        {verses.map((v) => (
                            <div
                                key={v.ref}
                                className="bg-black p-8 hover:bg-[#080808] transition-colors flex flex-col"
                                data-testid={`verse-${v.ref.replace(/\W+/g, "-").toLowerCase()}`}
                            >
                                <BookOpen size={20} className="text-red-600" strokeWidth={1.5} />
                                <p className="mt-6 text-white/85 italic leading-relaxed flex-1">"{v.quote}"</p>
                                <p className="mt-6 text-xs font-stencil tracking-[0.3em] text-red-600">{v.ref}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ LIES vs TRUTH ═══════════════ */}
            <section className="bg-[#050505] py-24 md:py-32 border-b border-white/10" data-testid="freetools-lies">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Eyebrow>The Reframe</Eyebrow>
                    <SectionTitle accent="for this.">Have a word ready</SectionTitle>
                    <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
                        When the lie hits, you don't argue with it. You speak the truth louder. Here are a few. The PDF has 30.
                    </p>

                    <div className="mt-16 space-y-3">
                        {lies.map((row, i) => (
                            <div
                                key={i}
                                className="grid md:grid-cols-12 items-stretch border border-white/10 hover:border-red-700/50 transition-colors group"
                                data-testid={`lie-row-${i}`}
                            >
                                {/* Lie side */}
                                <div className="md:col-span-5 p-6 md:p-8 bg-black flex items-start gap-3 border-b md:border-b-0 md:border-r border-white/10">
                                    <X size={18} className="text-white/40 mt-1 flex-shrink-0" />
                                    <div>
                                        <div className="font-stencil text-[10px] tracking-widest text-white/30 mb-2">THE LIE</div>
                                        <p className="text-white/55 line-through decoration-red-700/40 decoration-2">{row.lie}</p>
                                    </div>
                                </div>
                                {/* Truth side */}
                                <div className="md:col-span-7 p-6 md:p-8 bg-gradient-to-r from-[#0d0d0d] to-black flex items-start gap-3">
                                    <Repeat size={18} className="text-red-600 mt-1 flex-shrink-0 group-hover:rotate-180 transition-transform duration-500" />
                                    <div className="flex-1">
                                        <div className="font-stencil text-[10px] tracking-widest text-red-600 mb-2">THE TRUTH — SAY IT</div>
                                        <p className="font-display text-xl md:text-2xl uppercase tracking-tight leading-tight">{row.truth}</p>
                                        <p className="mt-2 text-xs font-stencil tracking-[0.3em] text-red-600">{row.ref}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <p className="text-white/50 italic">…and 22 more in the PDF.</p>
                    </div>
                </div>
            </section>

            {/* ═══════════════ HOW TO USE ═══════════════ */}
            <section className="bg-black py-24 md:py-32 border-b border-white/10" data-testid="freetools-howto">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Eyebrow>The Practice</Eyebrow>
                    <SectionTitle accent="in 4 steps.">How to use it</SectionTitle>

                    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                        {steps.map((s) => (
                            <div
                                key={s.n}
                                className="bg-black p-8 flex flex-col hover:bg-[#080808] transition-colors"
                                data-testid={`step-${s.n}`}
                            >
                                <div className="font-display text-6xl text-red-600 leading-none">{s.n}</div>
                                <h3 className="mt-6 font-display text-xl uppercase tracking-tight leading-tight">{s.title}</h3>
                                <p className="mt-4 text-white/65 leading-relaxed text-sm flex-1">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════ THE DOWNLOAD ═══════════════ */}
            <section
                id="get-pdf"
                className="relative bg-black py-24 md:py-32 border-b border-white/10 scroll-mt-24"
                data-testid="freetools-download-section"
            >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(185,28,28,0.18),transparent_65%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Eyebrow testId="freetools-download-eyebrow">Take the Field Manual</Eyebrow>
                        <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            Plant the truth. <br />
                            <span className="text-red-600">Pull the lies.</span>
                        </h2>
                        <p className="mt-8 text-white/70 leading-relaxed">
                            30 declarations. Every one anchored in Scripture. Designed to be printed, spoken, and rehearsed daily until the voice in your head changes its tune.
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto">
                        <PdfCard freebie={freebie} />
                    </div>
                </div>
            </section>

            {/* ═══════════════ FINAL WORD ═══════════════ */}
            <section className="bg-[#050505] py-24 md:py-32" data-testid="freetools-final">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                    <Quote size={36} className="text-red-600 mx-auto" strokeWidth={1.5} />
                    <p className="mt-8 font-display text-3xl md:text-5xl uppercase tracking-tight leading-[1.05]">
                        "Greater is He who is in you <br />
                        than he who is in <span className="text-red-600">the world.</span>"
                    </p>
                    <p className="mt-6 text-xs font-stencil tracking-[0.4em] text-white/50">1 JOHN 4:4</p>

                    <div className="mt-16">
                        <p className="text-white/65 leading-relaxed max-w-xl mx-auto">
                            You don't have to fix yourself first. You just have to start speaking what He already said about you.
                        </p>
                        <button
                            onClick={scrollToPdf}
                            className="mt-10 btn-primary"
                            data-testid="freetools-final-cta"
                        >
                            <Download size={18} /> Download the PDF — Free
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FreeTools;
